// client/src/pages/clientPortal/PortalTransactionPage.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useClientPortalApiClient } from '../../hooks/useClientPortalApiClient';

const CHECKLIST_ITEMS = [
    { key: 'attorney_review_deadline', label: 'Attorney Review' },
    { key: 'inspection_deadline', label: 'Inspection' },
    { key: 'financing_contingency_deadline', label: 'Financing Contingency' },
    { key: 'appraisal_deadline', label: 'Appraisal' },
    { key: 'earnest_money_due_date', label: 'Earnest Money Due' },
];

const PortalTransactionPage = () => {
    const { id } = useParams();
    const apiClient = useClientPortalApiClient();
    const [transaction, setTransaction] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        Promise.all([
            apiClient.get(`/api/client-portal/transactions/${id}`),
            apiClient.get(`/api/client-portal/transactions/${id}/documents`),
        ])
            .then(([t, docs]) => { setTransaction(t); setDocuments(docs); })
            .catch(err => setError(err.message || 'Failed to load this transaction.'))
            .finally(() => setIsLoading(false));
    }, [id, apiClient]);

    const handleDownload = async (doc) => {
        try {
            const response = await apiClient.get(`/api/client-portal/documents/${doc.document_id}/download`, { responseType: 'blob' });
            const file = new Blob([response.data]);
            const fileURL = URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = fileURL;
            link.download = doc.document_name;
            link.click();
            URL.revokeObjectURL(fileURL);
        } catch (err) {
            alert(err.message || 'Failed to download document.');
        }
    };

    if (isLoading) return <p className="text-slate-500">Loading...</p>;
    if (error) return <p className="text-red-500">{error}</p>;
    if (!transaction) return null;

    const today = new Date();

    return (
        <div className="space-y-6">
            <Link to="/client-portal/dashboard" className="text-indigo-600 hover:underline">&larr; Back to Your Transactions</Link>

            <div className="bg-white p-8 rounded-lg shadow">
                <div className="flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">{transaction.street_address}</h1>
                        <p className="text-slate-500">{transaction.city}, {transaction.state} {transaction.zip_code}</p>
                    </div>
                    <span className="px-3 py-1 text-sm font-semibold rounded-full bg-indigo-100 text-indigo-800">{transaction.status}</span>
                </div>
                {transaction.projected_close_date && (
                    <p className="mt-4 text-slate-600">Projected Closing: {new Date(transaction.projected_close_date).toLocaleDateString()}</p>
                )}
                {transaction.actual_close_date && (
                    <p className="text-slate-600">Closed: {new Date(transaction.actual_close_date).toLocaleDateString()}</p>
                )}
            </div>

            {CHECKLIST_ITEMS.some(item => transaction[item.key]) && (
                <div className="bg-white p-8 rounded-lg shadow">
                    <h2 className="text-xl font-bold text-slate-800 mb-4">What's Next</h2>
                    <ul className="space-y-3">
                        {CHECKLIST_ITEMS.filter(item => transaction[item.key]).map(item => {
                            const dueDate = new Date(transaction[item.key]);
                            const isPast = dueDate < today;
                            return (
                                <li key={item.key} className="flex justify-between border-b pb-2">
                                    <span className="text-slate-700">{item.label}</span>
                                    <span className={isPast ? 'text-slate-400' : 'text-slate-800 font-medium'}>
                                        {dueDate.toLocaleDateString()}{isPast && ' (passed)'}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}

            <div className="bg-white p-8 rounded-lg shadow">
                <h2 className="text-xl font-bold text-slate-800 mb-4">Documents</h2>
                {documents.length === 0 ? (
                    <p className="text-slate-500">No documents have been shared with you yet.</p>
                ) : (
                    <ul className="divide-y divide-gray-200">
                        {documents.map(doc => (
                            <li key={doc.document_id} className="py-3 flex justify-between items-center">
                                <div>
                                    <p className="font-medium text-slate-800">{doc.document_name}</p>
                                    <p className="text-sm text-slate-500">{doc.document_type}</p>
                                </div>
                                <button onClick={() => handleDownload(doc)} className="text-indigo-600 hover:underline text-sm">Download</button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default PortalTransactionPage;
